import './work-task-planning.scss';
import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Scheduler, { SchedulerRef, Resource, SchedulerTypes, AppointmentDragging, Toolbar as SchedulerToolbar, View } from 'devextreme-react/scheduler';
import { useScreenSize } from '../../utils/media-query';
import { getWorkTaskPlanning, getUnscheduledWorkTasks } from '../../api/workTask';
import DataSource from 'devextreme/data/data_source';
import { useAuth } from '../../contexts/auth';
import { Popup, ToolbarItem } from 'devextreme-react/popup';
import { Appointment, WorkTaskForm, WorkTaskMap } from '../../components';
import ContextMenu, { type ContextMenuTypes } from 'devextreme-react/context-menu';
import ODataStore from 'devextreme/data/odata/store';
import axios from 'axios';
import { confirm } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';
import moment from 'moment';
import Drawer from 'devextreme-react/drawer';
import ScrollView from 'devextreme-react/scroll-view';
import Draggable from 'devextreme-react/draggable';
import Button from 'devextreme-react/button';
import NumberBox from 'devextreme-react/number-box';
import SelectBox from 'devextreme-react/select-box';
import Switch from 'devextreme-react/switch';
import { Avatar } from '../../components';

interface SchedulerSettings {
  startDayHour: number;
  endDayHour: number;
  cellDuration: number;
  firstDayOfWeek: number;
  showAllDayPanel: boolean;
  defaultView: string;
}

const DEFAULT_SETTINGS: SchedulerSettings = {
  startDayHour: 7,
  endDayHour: 18,
  cellDuration: 30,
  firstDayOfWeek: 1,
  showAllDayPanel: true,
  defaultView: 'workWeek',
};

const CELL_DURATION_OPTIONS = [
  { value: 15, text: '15 minuten' },
  { value: 30, text: '30 minuten' },
  { value: 60, text: '60 minuten' },
];

const FIRST_DAY_OPTIONS = [
  { value: 0, text: 'Zondag' },
  { value: 1, text: 'Maandag' },
  { value: 6, text: 'Zaterdag' },
];

const DEFAULT_VIEW_OPTIONS = [
  { value: 'day', text: 'Dag' },
  { value: 'workWeek', text: 'Werkweek' },
  { value: 'timelineWorkWeek', text: 'Timeline Werkweek' },
  { value: 'month', text: 'Maand' },
];

function loadSchedulerSettings(tenantName: string): SchedulerSettings {
  try {
    const raw = localStorage.getItem(`schedulerSettings_${tenantName}`);
    if (raw) return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return { ...DEFAULT_SETTINGS };
}

function saveSchedulerSettings(tenantName: string, settings: SchedulerSettings) {
  localStorage.setItem(`schedulerSettings_${tenantName}`, JSON.stringify(settings));
}
type ContextMenuItem = ContextMenuTypes.Item & { onItemClick?: (e: ContextMenuTypes.ItemClickEvent) => void };
export const WorkTasksPlanning = React.memo(() => {
  const navigate = useNavigate();
  const { isXSmall } = useScreenSize();
  const { user, signOut } = useAuth();
  const schedulerRef = useRef<SchedulerRef>(null);
  const [workTaskPopupOpen, setWorkTaskPopup] = useState<boolean>(false);
  const [tasks, setTasks] = useState<DataSource>();
  const [employeeData, setEmployeeData] = useState<DataSource>();
  const [workTaskOid, setWorkTaskOid] = useState<string>('');
  const [appointmentData, setAppointmentData] = useState<Date[]>([]);
  const [visibleDateRange, setVisibleDateRange] = useState<{startDate: Date, endDate: Date} | null>({ startDate: new Date(), endDate: new Date() });
  const [contextMenuItems, setContextMenuItems] = useState<ContextMenuItem[]>([]);
  const [unscheduledTasks, setUnscheduledTasks] = useState<any[]>([]);
  const [drawerOpened, setDrawerOpened] = useState<boolean>(true);
  const [isUnscheduledMode, setIsUnscheduledMode] = useState<boolean>(false);
  const [mapOpened, setMapOpened] = useState<boolean>(false);
  const [mapFullWidth, setMapFullWidth] = useState<boolean>(false);
  const [rawTasks, setRawTasks] = useState<any[]>([]);
  const [defaultEmployeeOid, setDefaultEmployeeOid] = useState<string>('');
  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);
  const tenantKey = user?.tenantName || 'default';
  const [schedulerSettings, setSchedulerSettings] = useState<SchedulerSettings>(() => loadSchedulerSettings(tenantKey));
  // Map saved view type to display name for initial state
  const getViewDisplayName = (typeValue: string): string => {
    const viewMap: Record<string, string> = {
      'day': 'Dag',
      'workWeek': 'Werkweek',
      'timelineWorkWeek': 'Timeline Werkweek',
      'month': 'Maand',
    };
    return viewMap[typeValue] || typeValue;
  };
  const [currentViewName, setCurrentViewName] = useState<string>(() => getViewDisplayName(schedulerSettings.defaultView));
  // Persist settings when they change
  const updateSettings = useCallback((patch: Partial<SchedulerSettings>) => {
    setSchedulerSettings(prev => {
      const next = { ...prev, ...patch };
      saveSchedulerSettings(tenantKey, next);
      return next;
    });
  }, [tenantKey]);

  // Track current view for timeline data stretching
  const onOptionChanged = useCallback((e) => {
    if (e.name === 'currentView') {
      setCurrentViewName(e.value);
    }
  }, []);

  // In timeline view, stretch tasks visually to full-day (display only, DB times unchanged)
  const schedulerDataSource = useMemo(() => {
    if (!rawTasks?.length || !tasks) return tasks;
    if (currentViewName !== 'Timeline Werkweek') return tasks;
    return new DataSource({
      store: rawTasks.map(t => {
        const start = new Date(t.StartDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(t.StartDate);
        end.setHours(23, 59, 59, 999);
        return { ...t, RealStartDate: t.StartDate, RealEndDate: t.EndDate, StartDate: start, EndDate: end };
      }),
      paginate: false,
    });
  }, [rawTasks, currentViewName, tasks]);

  // Memoize the ODataStore to prevent recreation on every render
  const storeWorkTask = useMemo(() => new ODataStore({
    key: 'Oid',
    keyType: 'Guid',
    url: `${process.env.REACT_APP_URL}/api/odata/WorkTask`,
    version: 4,
    beforeSend: function(e) {
      e.headers = {
        'Authorization': `Bearer ${user?.token}`
      };
    },
  }), [user?.token]);

  // Resource cell renderer for timeline view (Bryntum-style employee panel)
  const renderResourceCell = useCallback((data) => {
    const employeeName = data.text || 'Medewerker';
    const employeeColor = data.color || '#9e9e9e';
    return (
      <div className='resource-cell'>
        <Avatar name={employeeName} backgroundColor={employeeColor} size={32} />
        <span className='resource-cell-name'>{employeeName}</span>
      </div>
    );
  }, []);

  // Groups for timeline view only
  const timelineGroups = useMemo(() => ['EmployeeOid'], []);

  // Memoize the date range key to prevent unnecessary re-renders
  const dateRangeKey = useMemo(() => {
    if (!visibleDateRange) return null;
    return `${visibleDateRange.startDate.getTime()}-${visibleDateRange.endDate.getTime()}`;
  }, [visibleDateRange]);

  // 📦 Fetch tasks when visible date range changes (triggered after onContentReady)
  useEffect(() => {
    if (visibleDateRange && user?.token) {
      getWorkTaskPlanning(visibleDateRange?.startDate, visibleDateRange?.endDate, user.token).then(tasksList => {
        setRawTasks(tasksList || []);
        setTasks(new DataSource({
          store: tasksList,
          paginate: false
        }));
      });
    }
  }, [dateRangeKey, user?.token, visibleDateRange]);

  // 📦 Fetch unscheduled tasks
  const fetchUnscheduledTasks = useCallback(() => {
    if (user?.token) {
      getUnscheduledWorkTasks(user.token).then(tasksList => {
        setUnscheduledTasks(tasksList || []);
      }).catch(() => {
        setUnscheduledTasks([]);
      });
    }
  }, [user?.token]);

  useEffect(() => {
    fetchUnscheduledTasks();
  }, [fetchUnscheduledTasks]);

  // 📦 Initial data load and employee datasource setup
  useEffect(() => {
    if (!user?.token) return;
    const store = new ODataStore({
      version: 4,
      key: 'Oid',
      keyType: 'Guid',
      url: `${process.env.REACT_APP_URL}/api/odata/Employee`,
      errorHandler: (error) => {
        if (error.httpStatus === 401) {
          signOut(true);
        }
      },
      beforeSend: (e) => {
        e.headers = {
          'Authorization': `Bearer ${user?.token}`
        };
      }
    });
    store.load({
      select: ['Oid', 'SchedulerColor', 'FullName', 'Active'],
      filter: ['Active', '=', true]
    }).then((data: any[]) => {
      // Convert Oid to string to ensure it matches task EmployeeOid (also a string)
      const employees = data.map(e => ({
        ...e,
        Oid: String(e.Oid),
      }));
      setEmployeeData(new DataSource({ store: employees, paginate: false }));
    });
  }, [user?.token, signOut]);

  // Restore scheduler state from localStorage after mount
  useEffect(() => {
    const schedulerStorageKey = `schedulerState_${user?.tenantName || 'default'}`;
    const savedState = localStorage.getItem(schedulerStorageKey);
    if (savedState && schedulerRef.current) {
      try {
        const { currentDate, currentView } = JSON.parse(savedState);
        const instance = schedulerRef.current.instance();
        if (currentDate) {
          instance.option('currentDate', new Date(currentDate));
        }
        if (currentView) {
          instance.option('currentView', currentView);
        }
        // Clear saved state after restoring
        localStorage.removeItem(schedulerStorageKey);
      } catch (e) {
        // silent
      }
    }
  }, [tasks, employeeData]);

  const onContentReady = useCallback((e) => {

    // Capture initial visible date range
    const startViewDate = e.component.getStartViewDate();
    const endViewDate = e.component.getEndViewDate();

    // Set time to 00:00:00 for consistent comparisons
    const normalizedStartDate = new Date(startViewDate);
    normalizedStartDate.setHours(0, 0, 0, 0);

    const normalizedEndDate = new Date(endViewDate);
    normalizedEndDate.setHours(0, 0, 0, 0);

    // Only update if the dates actually changed
    setVisibleDateRange(prevRange => {
      if (!prevRange ||
          prevRange.startDate.getTime() !== normalizedStartDate.getTime() ||
          prevRange.endDate.getTime() !== normalizedEndDate.getTime()) {
        return {
          startDate: normalizedStartDate,
          endDate: normalizedEndDate
        };
      }
      return prevRange;
    });

  }, []);

  const onAppointmentUpdating = useCallback(async(e: SchedulerTypes.AppointmentUpdatingEvent) => {
    const toLocalIso = (date) => {
      if (!date) return undefined;
      const d = date instanceof Date ? date : new Date(date);
      const pad = (n) => n.toString().padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    };
    const newData = e.newData as any;
    const oldData = e.oldData as any;
    const workTaskOid = newData?.WorkTaskOid ?? oldData?.WorkTaskOid;
    if (!workTaskOid) {
      e.cancel = true;
      notify('De opdracht kon niet worden bijgewerkt: identificatie ontbreekt.', 'error', 2500);
      return;
    }

    // Timeline appointments are stretched to a full day for display. Shift the
    // original real times by the dragged day delta instead of persisting 00:00–23:59.
    let startDate = newData?.StartDate ?? oldData?.StartDate;
    let endDate = newData?.EndDate ?? oldData?.EndDate;
    const realStart = oldData?.RealStartDate ? new Date(oldData.RealStartDate) : null;
    const realEnd = oldData?.RealEndDate ? new Date(oldData.RealEndDate) : null;
    const oldDisplayStart = oldData?.StartDate ? new Date(oldData.StartDate) : null;
    const newDisplayStart = newData?.StartDate ? new Date(newData.StartDate) : null;

    if (realStart && realEnd && oldDisplayStart && newDisplayStart) {
      const oldDay = new Date(
        oldDisplayStart.getFullYear(),
        oldDisplayStart.getMonth(),
        oldDisplayStart.getDate());
      const newDay = new Date(
        newDisplayStart.getFullYear(),
        newDisplayStart.getMonth(),
        newDisplayStart.getDate());
      const deltaDays = Math.round((newDay.getTime() - oldDay.getTime()) / 86_400_000);
      const shift = (value: Date) => {
        const shifted = new Date(value);
        shifted.setDate(shifted.getDate() + deltaDays);
        return shifted;
      };
      startDate = shift(realStart);
      endDate = shift(realEnd);
    }

    if (!startDate || !endDate) {
      e.cancel = true;
      notify('De opdracht kon niet worden bijgewerkt: planningstijden ontbreken.', 'error', 2500);
      return;
    }

    const sendEmail = await confirm('Wilt u een e-mailnotificatie naar de klant sturen?', 'E-mail versturen');
    try {
      await axios.put(`/api/WorkTask/Update/${workTaskOid}`, {
        TaskStartDate: toLocalIso(startDate),
        TaskEndDate: toLocalIso(endDate),
        TaskAllDay: newData?.IsAllDay ?? oldData?.IsAllDay ?? false,
        SendEmail: sendEmail,
      }, {
        headers: { 'Authorization': `Bearer ${user?.token}` }
      });
    } catch {
      e.cancel = true;
      notify('Bijwerken van de planning is mislukt.', 'error', 2500);
      return;
    }
    getWorkTaskPlanning(visibleDateRange?.startDate, visibleDateRange?.endDate, user?.token).then(tasksList => {
      setRawTasks(tasksList || []);
      setTasks(new DataSource({
        store: tasksList,
        paginate: false
      }));
    });
  }, [visibleDateRange?.startDate, visibleDateRange?.endDate, user?.token]);

  const onAppointmentDeleting = useCallback(async(e: SchedulerTypes.AppointmentDeletingEvent) => {
    await storeWorkTask.remove(e.appointmentData.WorkTaskOid);
    getWorkTaskPlanning(visibleDateRange?.startDate, visibleDateRange?.endDate, user?.token).then(tasksList => {
      setRawTasks(tasksList || []);
      setTasks(new DataSource({
        store: tasksList,
        paginate: false
      }));
    });
  }, [storeWorkTask, visibleDateRange?.startDate, visibleDateRange?.endDate, user?.token]);

  const onAppointmentFormOpening = useCallback((e: SchedulerTypes.AppointmentFormOpeningEvent) => {
    setWorkTaskOid(e.appointmentData?.WorkTaskOid);
    setWorkTaskPopup(true);
    // Use original times if available (timeline view stores them as RealStartDate/RealEndDate)
    const data = e.appointmentData as any;
    const startDate = data?.RealStartDate || data?.StartDate;
    const endDate = data?.RealEndDate || data?.EndDate;
    setAppointmentData([startDate, endDate]);
    // Prefill employee when clicking a cell in a grouped (timeline) view
    const empOid = data?.EmployeeOid;
    setDefaultEmployeeOid(empOid ? String(empOid) : '');
    e.cancel = true;
  }, []);

  const workTaskHidePopup = useCallback(() => {
    setWorkTaskPopup(false);
    setWorkTaskOid('');
    setAppointmentData([]);
    setDefaultEmployeeOid('');
    setIsUnscheduledMode(false);
    getWorkTaskPlanning(visibleDateRange?.startDate, visibleDateRange?.endDate, user?.token).then(tasksList => {
      setRawTasks(tasksList || []);
      setTasks(new DataSource({
        store: tasksList,
        paginate: false
      }));
    });
    fetchUnscheduledTasks();
  }, [visibleDateRange?.startDate, visibleDateRange?.endDate, user?.token, fetchUnscheduledTasks]);

  // Repaint scheduler after layout change so events adjust to new width
  const repaintScheduler = useCallback(() => {
    setTimeout(() => {
      schedulerRef.current?.instance().repaint();
    }, 350); // wait for CSS transition to finish
  }, []);

  // Toggle drawer visibility
  const toggleDrawer = useCallback(() => {
    setDrawerOpened(prev => !prev);
    repaintScheduler();
  }, [repaintScheduler]);

  // Toggle map panel visibility
  const toggleMap = useCallback(() => {
    setMapOpened(prev => {
      if (prev) setMapFullWidth(false);
      return !prev;
    });
    repaintScheduler();
  }, [repaintScheduler]);

  // Toggle map full width
  const toggleMapFullWidth = useCallback(() => {
    setMapFullWidth(prev => !prev);
    repaintScheduler();
  }, [repaintScheduler]);

  // Navigate to task details from map popup
  const onMapTaskClick = useCallback((workTaskOid: string) => {
    // Save scheduler state before navigating
    if (schedulerRef.current) {
      const instance = schedulerRef.current.instance();
      const schedulerState = {
        currentDate: instance.option('currentDate'),
        currentView: instance.option('currentView')
      };
      localStorage.setItem(`schedulerState_${user?.tenantName || 'default'}`, JSON.stringify(schedulerState));
    }
    navigate('/worktask-details', { state: { workTaskOid, backTo: 'workTasksPlanning', relationOid: 'relationOid' } });
  }, [navigate, user?.tenantName]);

  // Open popup for creating unscheduled task
  const openUnscheduledTaskPopup = useCallback(() => {
    setIsUnscheduledMode(true);
    setWorkTaskOid('');
    setAppointmentData([]);
    setWorkTaskPopup(true);
  }, []);

  // Handle drag and drop from unscheduled list to scheduler
  const onAppointmentAdd = useCallback(async(e) => {
    // itemData is set in onDragStart to contain our task
    const taskData = e.itemData;
    if (!taskData || !taskData.WorkTaskOid) {
      return;
    }

    // Get the scheduler instance to access cell data
    const schedulerInstance = e.toComponent;

    // Get coordinates from the drop event
    const dropEvent = e.event;
    let startDate = new Date();
    let endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // 1 hour default

    try {
      // Try to get cell data from coordinates
      if (schedulerInstance && dropEvent) {
        const cellData = schedulerInstance.getTargetCellData(dropEvent);
        if (cellData) {
          startDate = cellData.startDate;
          endDate = cellData.endDate;
        }
      }
    } catch (error) {
      // silent
    }

    // Prevent the default add behavior (since we're opening a form instead)
    e.cancel = true;

    // Open the form popup with the task so user can assign employees
    setWorkTaskOid(taskData.WorkTaskOid);
    setAppointmentData([startDate, endDate]);
    setIsUnscheduledMode(false);
    setWorkTaskPopup(true);
  }, []);

  // Render function for the unscheduled tasks sidebar
  const deleteUnscheduledTask = useCallback(async(task: any) => {
    const confirmed = await confirm(
      `Taak "${task.TaskDescription || task.RelationName}" verwijderen?`,
      'Bevestigen');
    if (!confirmed) {
      return;
    }

    try {
      await storeWorkTask.remove(task.WorkTaskOid);
      fetchUnscheduledTasks();
    } catch {
      notify('Verwijderen van de ongeplande taak is mislukt.', 'error', 2500);
    }
  }, [storeWorkTask, fetchUnscheduledTasks]);

  const formatTaskDateRange = useCallback((task: any) => {
    if (!task?.TaskStartDate) {
      return null;
    }
    const start = moment(task.TaskStartDate);
    const end = task.TaskEndDate ? moment(task.TaskEndDate) : null;
    if (!end) {
      return start.format('DD-MM-YYYY HH:mm');
    }
    if (start.isSame(end, 'day')) {
      return `${start.format('DD-MM-YYYY HH:mm')} - ${end.format('HH:mm')}`;
    }
    return `${start.format('DD-MM-YYYY HH:mm')} - ${end.format('DD-MM-YYYY HH:mm')}`;
  }, []);

  const renderUnscheduledTasksSidebar = useCallback(() => {
    return (
      <div className='unscheduled-tasks-sidebar'>
        <div className='sidebar-header'>
          <h3>Ongeplande taken</h3>
          <div className='header-actions'>
            <span className='task-count'>{unscheduledTasks.length}</span>
            <Button
              icon='add'
              hint='Nieuwe ongeplande taak'
              onClick={openUnscheduledTaskPopup}
              stylingMode='text'
            />
          </div>
        </div>
        <ScrollView height='calc(100% - 50px)' showScrollbar='onHover'>
          <div className='unscheduled-tasks-list' id='unscheduled-list'>
            {unscheduledTasks.map((task) => (
              <Draggable
                key={task.WorkTaskOid}
                group='appointmentsGroup'
                data={task}
                clone
                onDragStart={(e) => {
                  e.itemData = e.fromData;
                }}
                onDragEnd={(e) => {
                  if (e.toData) {
                    e.cancel = true;
                  }
                }}
              >
                <div
                  className='unscheduled-task-item dx-card'
                  onClick={() => {
                    setWorkTaskOid(task.WorkTaskOid);
                    setAppointmentData([]);
                    setIsUnscheduledMode(true);
                    setWorkTaskPopup(true);
                  }}
                >
                  <Button
                    icon='trash'
                    stylingMode='text'
                    hint='Verwijderen'
                    onClick={(event) => {
                      event.event?.stopPropagation();
                      void deleteUnscheduledTask(task);
                    }}
                  />
                  <div className='task-relation'>{task.RelationName}</div>
                  <div className='task-description'>{task.TaskDescription}</div>
                  <div className='task-activity'>{task.ActivityName}</div>
                  {task.TaskStartDate && (
                    <div className='task-dates'>{formatTaskDateRange(task)}</div>
                  )}
                  <div className='task-status'>
                    {!task.HasDate && <span className='status-badge no-date'>Geen datum</span>}
                    {!task.HasEmployees && <span className='status-badge no-employee'>Geen medewerker</span>}
                  </div>
                </div>
              </Draggable>
            ))}
            {unscheduledTasks.length === 0 && (
              <div className='no-tasks-message'>Geen ongeplande taken</div>
            )}
          </div>
        </ScrollView>
      </div>
    );
  }, [unscheduledTasks, openUnscheduledTaskPopup, deleteUnscheduledTask, formatTaskDateRange]);

  // Memoize close button options to prevent recreation
  const closeButtonOptions = useMemo(() => ({
    text: 'Sluiten',
    onClick: workTaskHidePopup
  }), [workTaskHidePopup]);

  // Settings popup close button
  const settingsCloseButtonOptions = useMemo(() => ({
    text: 'Sluiten',
    onClick: () => setSettingsOpen(false)
  }), []);

  // Reset settings to defaults
  const resetSettings = useCallback(() => {
    setSchedulerSettings({ ...DEFAULT_SETTINGS });
    saveSchedulerSettings(tenantKey, DEFAULT_SETTINGS);
  }, [tenantKey]);

  const customizeDateNavigatorText = useCallback((e) => {
    return `Week ${moment(e.startDate).week()}, ${moment(e.startDate).format('DD-MMM-yyyy')} - ${moment(e.endDate).format('DD-MMM-yyyy')}`;
  }, []);

  const onAppointmentContextMenu = useCallback((event: SchedulerTypes.AppointmentContextMenuEvent) => {
    const { appointmentData } = event;

    // Save current scheduler state to localStorage
    if (schedulerRef.current) {
      const instance = schedulerRef.current.instance();
      const schedulerState = {
        currentDate: instance.option('currentDate'),
        currentView: instance.option('currentView')
      };
      localStorage.setItem(`schedulerState_${user?.tenantName || 'default'}`, JSON.stringify(schedulerState));
    }

    setContextMenuItems([
      {
        text: 'Open opdracht',
        onItemClick: () => navigate('/worktask-details', { state: { workTaskOid: appointmentData.WorkTaskOid, backTo: 'workTasksPlanning', relationOid: 'relationOid' } }),
      },
    ]);
  }, [navigate]);

  const onContextMenuItemClick = (e: ContextMenuTypes.ItemClickEvent) => {
  (e.itemData as ContextMenuItem).onItemClick?.(e);
};

  // Memoize loading component to prevent recreation
  const LoadingComponent = useMemo(() => <div>Loading...</div>, []);

  return <div className='view-wrapper-calendar'>
    <div className='calendar-content'>
      <Button
        className='drawer-toggle-button'
        icon={drawerOpened ? 'chevrondoubleleft' : 'chevrondoubleright'}
        onClick={toggleDrawer}
        hint={drawerOpened ? 'Verberg ongeplande taken' : 'Toon ongeplande taken'}
      />

      <Button
        className={`map-toggle-button${mapOpened ? ' map-open' : ''}${mapFullWidth ? ' map-full-width' : ''}`}
        icon='map'
        onClick={toggleMap}
        hint={mapOpened ? 'Verberg kaart' : 'Toon kaart'}
      />
      <Drawer
        opened={drawerOpened}
        openedStateMode='shrink'
        position='left'
        revealMode='slide'
        component={renderUnscheduledTasksSidebar}
        closeOnOutsideClick={false}
        minSize={0}
        maxSize={280}
      >
        <div className={`scheduler-and-map${mapOpened ? ' map-visible' : ''}${mapFullWidth ? ' map-full-width' : ''}`}>
          <div className='main-content'>
            {tasks && employeeData ? (
              <Scheduler
                ref={schedulerRef}
                dataSource={schedulerDataSource}
                timeZone='Europe/Amsterdam'
                adaptivityEnabled={isXSmall}
                height='100%'
                defaultCurrentView={schedulerSettings.defaultView as any}
                startDateExpr='StartDate'
                groupByDate={false}
                endDateExpr='EndDate'
                allDayExpr='IsAllDay'
                maxAppointmentsPerCell={100}
                showCurrentTimeIndicator
                startDayHour={schedulerSettings.startDayHour}
                endDayHour={schedulerSettings.endDayHour}
                cellDuration={schedulerSettings.cellDuration}
                firstDayOfWeek={schedulerSettings.firstDayOfWeek as 0 | 1 | 2 | 3 | 4 | 5 | 6}
                allDayPanelMode={schedulerSettings.showAllDayPanel ? 'all' : 'hidden'}
                textExpr='Text'
                appointmentComponent={Appointment}
                onAppointmentContextMenu={onAppointmentContextMenu}
                onContentReady={onContentReady}
                onOptionChanged={onOptionChanged}
                onAppointmentUpdating={onAppointmentUpdating}
                onAppointmentFormOpening={onAppointmentFormOpening}
                onAppointmentDeleting={onAppointmentDeleting}
                customizeDateNavigatorText={customizeDateNavigatorText}
                resourceCellRender={renderResourceCell}>
                <View type='day' name='Dag' maxAppointmentsPerCell='unlimited' />
                <View type='workWeek' name='Werkweek' maxAppointmentsPerCell='unlimited' />
                <View
                  type='timelineWorkWeek'
                  name='Timeline Werkweek'
                  groupOrientation='vertical'
                  groups={timelineGroups}
                  maxAppointmentsPerCell='unlimited'
                  cellDuration={1440}
                  startDayHour={0}
                  endDayHour={24}
                />
                <View type='month' name='Maand' maxAppointmentsPerCell='unlimited' />
                <SchedulerToolbar
                  items={[
                    'dateNavigator',
                    'viewSwitcher',
                    {
                      widget: 'dxButton',
                      location: 'after',
                      options: {
                        icon: 'preferences',
                        hint: 'Planner instellingen',
                        onClick: () => setSettingsOpen(true),
                      },
                    },
                  ]}
                />
                <AppointmentDragging
                  group='appointmentsGroup'
                  onAdd={onAppointmentAdd}
                />
                <Resource
                  dataSource={employeeData}
                  fieldExpr='EmployeeOid'
                  valueExpr='Oid'
                  displayExpr='FullName'
                  colorExpr='SchedulerColor'
                  label='Medewerker'
                />
              </Scheduler>
            ) : (
              LoadingComponent
            )}
            <ContextMenu
              dataSource={contextMenuItems}
              width={200}
              target='.dx-scheduler-appointment'
              onItemClick={onContextMenuItemClick}
            />
            <Popup
              visible={settingsOpen}
              onHiding={() => setSettingsOpen(false)}
              dragEnabled={false}
              hideOnOutsideClick
              showCloseButton={false}
              showTitle
              title='Planner instellingen'
              width={420}
              height='auto'>
              <ToolbarItem
                widget='dxButton'
                toolbar='top'
                location='after'
                options={settingsCloseButtonOptions} />
              <div className='scheduler-settings-form'>
                <div className='settings-row'>
                  <label>Startuur</label>
                  <NumberBox
                    value={schedulerSettings.startDayHour}
                    min={0}
                    max={schedulerSettings.endDayHour - 1}
                    showSpinButtons
                    onValueChanged={(e) => updateSettings({ startDayHour: e.value })}
                  />
                </div>
                <div className='settings-row'>
                  <label>Einduur</label>
                  <NumberBox
                    value={schedulerSettings.endDayHour}
                    min={schedulerSettings.startDayHour + 1}
                    max={24}
                    showSpinButtons
                    onValueChanged={(e) => updateSettings({ endDayHour: e.value })}
                  />
                </div>
                <div className='settings-row'>
                  <label>Celduur</label>
                  <SelectBox
                    items={CELL_DURATION_OPTIONS}
                    value={schedulerSettings.cellDuration}
                    valueExpr='value'
                    displayExpr='text'
                    onValueChanged={(e) => updateSettings({ cellDuration: e.value })}
                  />
                </div>
                <div className='settings-row'>
                  <label>Eerste dag van de week</label>
                  <SelectBox
                    items={FIRST_DAY_OPTIONS}
                    value={schedulerSettings.firstDayOfWeek}
                    valueExpr='value'
                    displayExpr='text'
                    onValueChanged={(e) => updateSettings({ firstDayOfWeek: e.value })}
                  />
                </div>
                <div className='settings-row'>
                  <label>Standaard weergave</label>
                  <SelectBox
                    items={DEFAULT_VIEW_OPTIONS}
                    value={schedulerSettings.defaultView}
                    valueExpr='value'
                    displayExpr='text'
                    onValueChanged={(e) => updateSettings({ defaultView: e.value })}
                  />
                </div>
                <div className='settings-row switch-row'>
                  <label>Hele-dag paneel tonen</label>
                  <Switch
                    value={schedulerSettings.showAllDayPanel}
                    onValueChanged={(e) => updateSettings({ showAllDayPanel: e.value })}
                  />
                </div>
                <div className='settings-actions'>
                  <Button
                    text='Standaard herstellen'
                    icon='revert'
                    stylingMode='outlined'
                    onClick={resetSettings}
                  />
                </div>
              </div>
            </Popup>
            <Popup
              visible={workTaskPopupOpen}
              dragEnabled={false}
              hideOnOutsideClick
              showCloseButton={false}
              showTitle
              title={isUnscheduledMode ? 'Ongeplande taak aanmaken' : 'Opdracht aanmaken'}
              width={600}
              height={650}>
              <ToolbarItem
                widget='dxButton'
                toolbar='top'
                location='after'
                options={closeButtonOptions} />
              <WorkTaskForm
                appointmentData={appointmentData}
                workTaskOid={workTaskOid}
                contractOid=''
                onComplete={workTaskHidePopup}
                allowUnscheduled={isUnscheduledMode}
                defaultEmployeeOid={defaultEmployeeOid} />
            </Popup>
          </div>
          {mapOpened && (
            <div className='map-panel'>
              <WorkTaskMap tasks={rawTasks} onTaskClick={onMapTaskClick} isFullWidth={mapFullWidth} onToggleFullWidth={toggleMapFullWidth} />
            </div>
          )}
        </div>
      </Drawer>
    </div>
  </div>;
});
